import type { Transaction as CodeMirrorTransaction } from "@codemirror/state";
import { useSelector } from "@xstate/react";
import { type Map, update } from "immutable";
import isEmpty from "lodash-es/isEmpty";
import { type Node } from "prosemirror-model";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTimeoutFn } from "react-use";

import { generateShortUuid } from "@moment/api-collab";
import { type TableDiff } from "@moment/api-collab/differential/sqlite";
import type { Transaction as ProseMirrorTransaction } from "@moment/api-collab/prosemirror-state";
import { schema } from "@moment/prosemirror-schema";

import { type GitDiff } from "../data/git/types";
import { useAppActor, useAppActorSelector } from "./machines/app/app.machine";
import { type GitShow } from "./machines/git/repository/git-repository.types";
import { type SyncMode, type SyncPlan, emptySyncPlan } from "./machines/sync/plans";
import {
	type InvitationUpdate,
	type PageMetadata,
	type UpdateUserPermission,
	type UpdateWorkspacePermission,
} from "./types";
import { type Flag, initialFlags } from "./types/feature-flags.types";
import { useStateMachineRuntimeBridge } from "./useStateMachineRuntimeBridge";

export const useAtlasActor = () => {
	const actor = useAppActorSelector((state) => state.context.atlasActorRef);
	return actor;
};

export const useAtlasSettingsState = () => {
	const actor = useAtlasActor();
	const settings = useSelector(actor, (snapshot) => snapshot?.context.state);
	return settings;
};

export const useAtlasEnvVars = () => {
	const actor = useAtlasActor();
	const envVars = useSelector(actor, (snapshot) => snapshot?.context.state.envVars);
	return envVars;
};

export const useAtlasStatus = () => {
	const actor = useAppActorSelector((state) => state.context.atlasActorRef);

	const isLoading = useSelector(actor, (state) => state?.matches("loading"));
	const isIdle = useSelector(actor, (state) => state?.matches("idle"));
	const isPersisting = useSelector(actor, (state) => {
		return state?.matches("persistSettings") || state?.matches("persistEnvVars");
	});
	const isError = useSelector(actor, (state) => state?.matches("error"));

	const isSuccess = useMemo(() => {
		return isIdle || isPersisting;
	}, [isIdle, isPersisting]);

	return {
		isLoading,
		isSuccess,
		isError,
	};
};

export const useDocumentBaseActor = (documentId: string) => {
	const documentActor = useDocumentActor(documentId);
	return useSelector(documentActor, (state) => state?.context.baseActorRef);
};

export const useDocumentBaseStream = (documentId: string) => {
	const baseActor = useDocumentBaseActor(documentId);
	return useStateMachineRuntimeBridge(`documentBaseStream-${documentId}`, baseActor, (state) => {
		return state?.context.store ?? null;
	});
};

export const useDocumentBaseError = (documentId: string) => {
	const baseActor = useDocumentBaseActor(documentId);
	return useSelector(baseActor, (state) => state?.context.error);
};

export const useDocumentBaseSchemaDiff = (
	documentId: string
): Map<string, TableDiff> | undefined => {
	const baseActor = useDocumentBaseActor(documentId);
	return useSelector(baseActor, (state) => state?.context.schemaDiff);
};

export const useTransferSyncState = (documentId: string) => {
	const documentMachine = useAppActorSelector(
		(state) => state.context.documentActorRefs[documentId]
	);

	const transferSyncActor = useSelector(
		documentMachine,
		(state) => state?.context.transferActorRef
	);

	const [isLoading, isError] = useSelector(transferSyncActor, (state) => {
		if (!state) {
			return [false, false];
		}
		const isLoading = state.matches("loading");
		const isError = state.matches("error");
		return [isLoading, isError];
	});

	const dirty = useSelector(transferSyncActor, (state) => state?.context.dirty);
	const currentWorkspace = useSelector(transferSyncActor, (state) => state?.context.workspace);
	const errorState = useSelector(transferSyncActor, (state) => state?.context.error);
	const remoteRepoExists = useSelector(
		transferSyncActor,
		(state) => state?.context.remoteRepoExists
	);
	const refresh = () => {
		transferSyncActor?.send({ type: "transfer.refresh" });
	};

	const attachWorkspace = (workspaceName: string) => {
		transferSyncActor?.send({
			type: "transfer.attachWorkspace",
			workspace: workspaceName,
			documentId,
		});
	};

	return {
		currentWorkspace,
		transferSyncActor,
		isLoading,
		dirty,
		isError,
		errorState,
		refresh,
		remoteRepoExists,
		attachWorkspace,
	};
};

export const useSharingSyncState = (documentId: string) => {
	const documentMachine = useAppActorSelector(
		(state) => state.context.documentActorRefs[documentId]
	);
	const sharingSyncActor = useSelector(
		documentMachine,
		(state) => state?.context.sharingActorRef
	);
	const syncActor = useSelector(documentMachine, (state) => state?.context.remoteSyncActorRef);

	const [isLoading, isError] = useSelector(sharingSyncActor, (state) => {
		if (!state) {
			return [false, false];
		}
		const isLoading = state.matches("loading");
		const isError = state.matches("error");
		return [isLoading, isError];
	});

	const dirty = useSelector(sharingSyncActor, (state) => state?.context.dirty);
	const permissions = useSelector(sharingSyncActor, (state) => state?.context.permissionState);
	const errorState = useSelector(sharingSyncActor, (state) => state?.context.error);
	const attachedWorkspace = useSelector(sharingSyncActor, (state) => state?.context.workspace);

	const refresh = () => {
		sharingSyncActor?.send({ type: "sharing.refresh" });
	};

	const updateUserPermission = ({ user, relation, type }: UpdateUserPermission) => {
		sharingSyncActor?.send({
			type: "sharing.permission.update.user",
			payload: { user, relation, type },
		});
	};

	const updateWorkspacePermission = ({ relation, type }: UpdateWorkspacePermission) => {
		sharingSyncActor?.send({
			type: "sharing.permission.update.workspace",
			payload: { relation, type },
		});
	};

	const updateInvite = ({ email, relation, type }: InvitationUpdate) => {
		sharingSyncActor?.send({
			type: "sharing.invite.update",
			payload: { email, relation, type },
		});
	};

	const attachWorkspace = (workspaceName: string) => {
		sharingSyncActor?.send({
			type: "sharing.attachWorkspace",
			workspace: workspaceName,
		});

		syncActor?.send({
			type: "sync.assignWorkspace",
			workspaceName,
		});
	};

	return {
		syncActor,
		sharingSyncActor,
		isLoading,
		dirty,
		permissions,
		isError,
		errorState,
		attachedWorkspace,
		refresh,
		updateUserPermission,
		updateWorkspacePermission,
		updateInvite,
		attachWorkspace,
	};
};

export const useFeatureFlags = () => {
	const actor = useAppActorSelector((state) => state.context.featureFlagActorRef);
	const contextFlags = useSelector(actor, (state) => state?.context.flags);

	const flags = useMemo(() => contextFlags ?? initialFlags, [contextFlags]);

	const setFlag = useCallback(
		(flag: Flag, enabled: boolean) => {
			actor?.send({ type: "featureFlag.update", payload: { id: flag, enabled } });
		},
		[actor]
	);

	return { flags, setFlag };
};

export const useIsFeatureFlagEnabled = (flag: Flag) => {
	const { flags } = useFeatureFlags();
	return flags[flag];
};

export const useWorkspaces = () => {
	const { send } = useAppActor();
	const workspaces = useAppActorSelector((state) => state.context.workspaces);

	return useMemo(
		() => ({
			data: Object.values(workspaces),
			refetch: () => {
				send({ type: "workspaceList.refetch" });
			},
		}),
		[workspaces, send]
	);
};

export const useOwnedWorkspaces = () => {
	const { data: workspaces } = useWorkspaces();
	return useMemo(() => workspaces.filter((workspace) => workspace.owner === true), [workspaces]);
};

export const useWorkspacesWithAccess = () => {
	const { data: workspaces } = useWorkspaces();
	return useMemo(
		() =>
			workspaces.filter(
				(workspace) => workspace.role === "owner" || workspace.role === "member"
			),
		[workspaces]
	);
};

export const usePersonalWorkspace = (loggedInUser) => {
	const { data: workspaces } = useWorkspaces();

	return useMemo(
		() =>
			workspaces.find(
				(workspace) => workspace.name === loggedInUser?.nickname,
				[loggedInUser?.nickname]
			),
		[workspaces, loggedInUser?.nickname]
	);
};

export const useDocuments = () => {
	const { send } = useAppActor();
	const documents = useAppActorSelector((state) => state.context.documents);

	return useMemo(
		() => ({
			data: Object.values(documents),
			refetch: () => {
				send({ type: "documentList.refetch" });
			},
		}),
		[documents, send]
	);
};

// Returning an undefined intentionally.
// If undefined, it means the document list is still loading.
export const useDocumentExists = (documentId: string) => {
	const [isTimeoutExpired, setTimeoutExpired] = useState(false);

	const documents = useAppActorSelector((state) => state.context.documents);

	useTimeoutFn(() => {
		setTimeoutExpired(true);
	}, 1000);

	return useMemo(() => {
		if (isEmpty(documents) && !isTimeoutExpired) {
			return undefined;
		}
		return documents[documentId] !== undefined;
	}, [documents, documentId, isTimeoutExpired]);
};

export const useDocumentActor = (documentId: string) => {
	const documentActor = useAppActorSelector(
		(state) => state.context.documentActorRefs[documentId]
	);

	return documentActor;
};

export const useDocumentMetadata = (documentId: string) => {
	const documentActor = useDocumentActor(documentId);

	const metadata = useSelector(documentActor, (state) => state?.context.metadata);

	return metadata;
};

export const useDocumentListItem = (documentId: string) => {
	const documentActor = useDocumentActor(documentId);

	const listItem = useSelector(documentActor, (state) => state?.context.listItem);

	return listItem;
};

export const useDocumentPageMetadataById = (_workspaceName: string, documentId: string) => {
	const document = useDocumentMetadata(documentId);
	return useMemo<Record<string, PageMetadata>>(() => {
		if (!document) {
			return {};
		}
		return document?.pagesMetadata.reduce((acc, page) => {
			acc[page.id] = page;
			return acc;
		}, {});
	}, [document]);
};

export const usePageActor = (documentId: string, pageId: string) => {
	const documentActor = useDocumentActor(documentId);

	const pageActor = useSelector(documentActor, (state) => state?.context.pageActorRefs[pageId]);

	return pageActor;
};

export const usePageData = (documentId: string, pageId: string) => {
	const pageActor = usePageActor(documentId, pageId);

	const pageData = useSelector(pageActor, (state) => state?.context.pageData);

	return pageData;
};

export const useRemoteSyncActor = (documentId: string, syncMode: SyncMode) => {
	const documentMachine = useAppActorSelector(
		(state) => state.context.documentActorRefs[documentId]
	);
	const remoteSync = useSelector(documentMachine, (state) => state?.context.remoteSyncActorRef);
	const trySyncPlan =
		useSelector(remoteSync, (state) => state?.context.trySyncPlan) || emptySyncPlan();
	const publishIfUnpublishedPlan = useSelector(
		remoteSync,
		(state) => state?.context.publishIfUnpublishedPlan
	);
	const shareSyncPlan = useSelector(remoteSync, (state) => state?.context.shareSyncPlan);
	const publishWorkspace = useSelector(remoteSync, (state) => state?.context.publishWorkspace);
	const emptyPlan = emptySyncPlan();
	const syncError = useSelector(remoteSync, (state) => state?.context.error);

	let syncPlan: SyncPlan | undefined = undefined;
	switch (syncMode) {
		case "try-sync":
			syncPlan = trySyncPlan;
			break;
		case "publish-if-unpublished":
			syncPlan = publishIfUnpublishedPlan;
			break;
		case "share-sync":
			syncPlan = shareSyncPlan;
			break;
		default:
			syncPlan = emptyPlan;
			break;
	}

	const canExecutePlan =
		useSelector(remoteSync, (state) =>
			state?.can({
				type: "sync.executePlan",
				syncMode,
			})
		) || false;

	const executePlan = (commitMessage: string) => {
		remoteSync?.send({
			type: "sync.executePlan",
			pendingCommitMessage: commitMessage,
			syncMode,
		});
	};

	const canUnpublishDocument =
		useSelector(remoteSync, (state) =>
			state?.can({
				type: "sync.unpublishDocument",
			})
		) || false;

	const unpublishDocument = () =>
		remoteSync?.send({
			type: "sync.unpublishDocument",
		});

	const [isIdle, isPlanning, isSyncing] = useSelector(remoteSync, (state) => {
		if (!state) {
			return [false, false, false];
		}
		const isGenerating = state.matches("generatingPlan");
		const isIdle = state.matches("idle");
		return [isIdle, isGenerating, !(isGenerating || isIdle)];
	});

	return {
		remoteSync,
		syncPlan: syncPlan!,
		canExecutePlan,
		executePlan,
		canUnpublishDocument,
		unpublishDocument,
		isIdle,
		isPlanning,
		isSyncing,
		publishWorkspace,
		syncError,
	};
};

export const useGitActor = (documentId: string) => {
	const git = useGitRepoActor(documentId);

	const refreshStatus = useCallback(() => git?.send({ type: "git.status" }), [git]);
	const refreshShow = useCallback(
		(commitHash: string) => git?.send({ type: "git.show", payload: { commitHash } }),
		[git]
	);
	const refreshDiff = useCallback(() => git?.send({ type: "git.diff" }), [git]);

	return { git, refreshStatus, refreshShow, refreshDiff };
};

export const useGitConfigActor = () => {
	const gitConfig = useAppActorSelector((state) => state.context.gitConfigActorRef);

	const data = useSelector(gitConfig, (state) => state?.context.config);
	const isLoading = useSelector(gitConfig, (state) => state?.matches("loading"));
	const isSuccess = useSelector(gitConfig, (state) => state?.matches("ready"));
	const isError = useSelector(gitConfig, (state) => state?.matches("error"));
	const refetch = useCallback(() => gitConfig?.send({ type: "gitConfig.refetch" }), [gitConfig]);
	const updateKey = useCallback(
		(key: string, value: string | number | null) =>
			gitConfig?.send({ type: "gitConfig.updateKey", key, value }),
		[gitConfig]
	);

	return {
		data,
		isLoading,
		isSuccess,
		isError,
		refetch,
		updateKey,
	};
};

const useGitRepoActor = (documentId: string) => {
	const documentMachine = useDocumentActor(documentId);
	const git = useSelector(documentMachine, (state) => state?.context.gitRepositoryActorRef);

	return git;
};

export const useGitLog = (documentId: string) => {
	const gitRepoActor = useGitRepoActor(documentId);
	const gitLog = useSelector(gitRepoActor, (state) => state?.context.history);

	useEffect(() => {
		gitRepoActor?.send({ type: "git.log" });
	}, [gitRepoActor]);

	return gitLog;
};

export const useGitStatus = (documentId: string) => {
	const gitRepoActor = useGitRepoActor(documentId);
	const [isError, setIsError] = useState(false);

	const gitStatus = useSelector(gitRepoActor, (state) => state?.context.status);

	useEffect(() => {
		const successSubscription = gitRepoActor?.on("git.status.done", () => {
			setIsError(false);
		});

		const errorSubscription = gitRepoActor?.on("git.status.error", () => {
			setIsError(true);
		});

		return () => {
			successSubscription?.unsubscribe();
			errorSubscription?.unsubscribe();
		};
	});

	const refreshStatus = useCallback(
		() => gitRepoActor?.send({ type: "git.status" }),
		[gitRepoActor]
	);

	const isPending = useSelector(gitRepoActor, (state) => state?.matches({ queue: "processing" }));

	return {
		data: gitStatus,
		refetch: refreshStatus,
		isPending,
		isError,
	};
};

export const useGitDiff = (documentId: string) => {
	const { git } = useGitActor(documentId);
	const [gitDiff, setGitDiff] = useState<GitDiff | undefined>(undefined);
	const [isLoading, setIsLoading] = useState(true);
	const [isError, setIsError] = useState(false);

	useEffect(() => {
		setIsLoading(true);
		setIsError(false);

		const successSubscription = git?.on("git.diff.done", (event) => {
			setGitDiff(event.output as GitDiff);
			setIsLoading(false);
		});

		const errorSubscription = git?.on("git.diff.error", () => {
			setGitDiff(undefined);
			setIsLoading(false);
			setIsError(true);
		});

		git?.send({ type: "git.diff" });

		return () => {
			successSubscription?.unsubscribe();
			errorSubscription?.unsubscribe();
		};
	}, [git]);

	const refetch = useCallback(() => {
		git?.send({ type: "git.diff" });
	}, [git]);

	return [
		{
			data: gitDiff,
			isLoading,
			isError,
			isSuccess: !isError && !isLoading && gitDiff !== undefined,
		},
		refetch,
	] as const;
};

export const useGitShow = (documentId: string, commitHash: string = "HEAD") => {
	const { git } = useGitActor(documentId);
	const [gitShow, setGitShow] = useState<GitShow | undefined>(undefined);
	const [isLoading, setIsLoading] = useState(true);
	const [isError, setIsError] = useState(false);

	useEffect(() => {
		setIsLoading(true);
		setIsError(false);

		const successSubscription = git?.on("git.show.done", (event) => {
			setGitShow(event.output as GitShow);
			setIsLoading(false);
		});

		const errorSubscription = git?.on("git.show.error", () => {
			setGitShow(undefined);
			setIsLoading(false);
			setIsError(true);
		});

		git?.send({ type: "git.show", payload: { commitHash } });

		return () => {
			successSubscription?.unsubscribe();
			errorSubscription?.unsubscribe();
		};
	}, [git, commitHash]);

	const refetch = useCallback(() => {
		git?.send({ type: "git.show", payload: { commitHash } });
	}, [git, commitHash]);

	return [
		{
			data: gitShow,
			isLoading,
			isError,
			isSuccess: !isError && !isLoading && gitShow !== undefined,
		},
		refetch,
	] as const;
};

export const useTransactionDispatched = (documentId: string, pageId: string) => {
	const pageActor = usePageActor(documentId, pageId);
	return useCallback(
		(args: {
			pageId: string;
			transaction: ProseMirrorTransaction | CodeMirrorTransaction;
			timestamp: Date;
		}) => {
			pageActor?.send({
				type: "page.transactionDispatched",
				payload: {
					transactionId: generateShortUuid(),
					transaction: args.transaction,
					timestamp: args.timestamp,
				},
			});
		},
		[pageActor]
	);
};

export const useCompiledCells = (documentId: string, pageId: string) => {
	const pageActor = usePageActor(documentId, pageId);
	return useSelector(pageActor, (state) => state?.context.compiledCells);
};

export const useSelectedComputeCellNodes = (documentId: string, pageId: string) => {
	const pageActor = usePageActor(documentId, pageId);
	const editorState = useSelector(pageActor, (state) => state?.context.editorState);

	return useMemo(() => {
		const nodes: Node[] = [];

		const selection = editorState?.selection;
		if (!selection) {
			return nodes;
		}

		editorState.doc.nodesBetween(selection.from, selection.to, (node) => {
			if (!node.attrs["id"] || node.type !== schema.nodes.compute_cell) {
				return true;
			}
			nodes.push(node);
			return true;
		});

		return nodes;
	}, [editorState]);
};

export const useDocumentWorkspaces = (documentId: string) => {
	const listItem = useDocumentListItem(documentId);

	return listItem?.workspaces;
};
